import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import Navbar from '@/components/Navbar';

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Voltar
        </Link>

        <div className="prose prose-invert max-w-none">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
            Política de Privacidade
          </h1>
          <p className="text-muted-foreground mb-8">
            Última atualização: Junho de 2026
          </p>

          <div className="space-y-8 text-muted-foreground">
            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">1. Introdução</h2>
              <p>
                O Intimate.pt ("nós", "nosso" ou "Plataforma") está comprometido em proteger a sua 
                privacidade. Esta Política de Privacidade explica como recolhemos, usamos, divulgamos 
                e protegemos as suas informações pessoais em conformidade com o Regulamento Geral de 
                Proteção de Dados (RGPD) e a legislação portuguesa.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">2. Responsável pelo Tratamento</h2>
              <p>
                O responsável pelo tratamento dos teus dados pessoais é:
              </p>
              <div className="mt-3 p-4 bg-surface rounded-lg border border-border">
                <p><strong className="text-foreground">Intimate.pt</strong></p>
                <p>Email: geral@pimentadocelingerie.pt</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">3. Dados que Recolhemos</h2>
              
              <h3 className="text-lg font-medium text-foreground mt-6 mb-3">3.1 Dados fornecidos por si</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-foreground">Dados de registo:</strong> nome, email, data de nascimento, palavra-passe (encriptada)</li>
                <li><strong className="text-foreground">Conteúdo:</strong> contos, comentários, confissões, avaliações</li>
                <li><strong className="text-foreground">Comunicações:</strong> mensagens enviadas através da Plataforma</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6 mb-3">3.2 Dados recolhidos automaticamente</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-foreground">Dados de utilização:</strong> páginas visitadas, contos lidos, tempo de leitura, progresso</li>
                <li><strong className="text-foreground">Dados técnicos:</strong> endereço IP, tipo de browser, sistema operativo, dispositivo</li>
                <li><strong className="text-foreground">Cookies:</strong> conforme descrito na nossa Política de Cookies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">4. Finalidades do Tratamento</h2>
              <p>Utilizamos os teus dados para:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Fornecer e manter a Plataforma</li>
                <li>Criar e gerir a tua conta</li>
                <li>Personalizar a sua experiência (recomendações, favoritos)</li>
                <li>Processar pagamentos e compras</li>
                <li>Comunicar consigo sobre a tua conta ou serviços</li>
                <li>Garantir a segurança e prevenir fraudes</li>
                <li>Cumprir obrigações legais</li>
                <li>Moderar conteúdo e garantir conformidade com os Termos</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">5. Base Legal do Tratamento</h2>
              <p>O tratamento dos teus dados baseia-se em:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li><strong className="text-foreground">Execução de contrato:</strong> para fornecer os serviços da Plataforma</li>
                <li><strong className="text-foreground">Consentimento:</strong> para comunicações de marketing e cookies não essenciais</li>
                <li><strong className="text-foreground">Interesses legítimos:</strong> para melhorar os serviços e garantir segurança</li>
                <li><strong className="text-foreground">Obrigação legal:</strong> para cumprir requisitos legais aplicáveis</li>
              </ul>
            </section>

            <section data-testid="privacy-analytics-section">
              <h2 className="text-xl font-semibold text-foreground mb-4">5-A. Estatísticas, Atividade e Leitura</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-foreground">Funcionamento da conta</strong> (execução do contrato): dados de registo, subscrição Premium, favoritos e o progresso de leitura guardado para poderes "continuar a ler".</li>
                <li><strong className="text-foreground">Segurança</strong> (interesse legítimo e obrigações legais): registos técnicos de acesso e prevenção de abuso.</li>
                <li><strong className="text-foreground">Atividade da conta</strong> (interesse legítimo, art. 6.º, n.º 1, al. f) do RGPD): quando usas a Intimate.pt com sessão iniciada, registamos apenas o dia em que a tua conta esteve ativa — nunca as páginas ou os conteúdos consultados. Usamos este dado só para estatísticas agregadas de utilização e retenção, nunca para criar perfis individuais. Os registos diários são eliminados automaticamente ao fim de 90 dias. Podes opor-te a qualquer momento em «A minha conta → Privacidade» ou escrevendo para geral@intimate.pt: deixamos de registar a tua atividade diária e apagamos o histórico existente.</li>
                <li><strong className="text-foreground">Estatísticas agregadas sem consentimento</strong>: contadores diários anónimos por secção do site, origem e tipo de dispositivo, sem cookies nem identificadores (ver Política de Cookies).</li>
                <li><strong className="text-foreground">Estatísticas com consentimento</strong>: cookie próprio de visitante e, se forem ativados, Google Analytics e Microsoft Clarity. Só com a tua autorização, que podes retirar a qualquer momento.</li>
                <li><strong className="text-foreground">Marketing</strong>: só com consentimento específico (ver secção 5-B). Não usamos o teu histórico de leitura para marketing nem para segmentação.</li>
              </ul>
              <p className="mt-3">
                Os hábitos de leitura de conteúdos de natureza sexual podem revelar informação sobre a vida sexual, uma categoria especial de dados
                (art. 9.º do RGPD). Por isso, não associamos as páginas que visitas à tua conta para estatísticas, e o progresso de leitura só é usado
                para o serviço que pediste e para contagens agregadas.
              </p>
            </section>

            <section data-testid="privacy-marketing-section">
              <h2 className="text-xl font-semibold text-foreground mb-4">5-B. Comunicações Promocionais</h2>
              <p>
                Só enviamos novidades sobre contos, séries, conteúdos exclusivos e campanhas a quem o autorizar expressamente (consentimento, art. 6.º, n.º 1, al. a) do RGPD),
                no registo ou em «A minha conta → Comunicações por e-mail». Guardamos a data, a origem e a versão do texto aceite.
              </p>
              <p className="mt-3">
                Podes retirar o consentimento a qualquer momento: na tua conta, na ligação «Cancelar subscrição» incluída em cada e-mail, ou escrevendo para geral@intimate.pt.
                A retirada não afeta a licitude dos envios anteriores. Os e-mails essenciais da conta (verificação, palavra-passe, subscrição) não dependem deste consentimento.
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li><strong className="text-foreground">Newsletters da Intimate.pt</strong>: compostas e enviadas a partir da nossa plataforma; a Brevo atua apenas como serviço técnico de entrega.</li>
                <li><strong className="text-foreground">Campanhas no Brevo</strong>: preparadas e enviadas diretamente na plataforma Brevo, para a qual exportamos apenas o e-mail e o nome de quem deu consentimento.</li>
                <li><strong className="text-foreground">Subcontratante</strong>: Brevo (Sendinblue SAS, França, UE), que atua por nossa conta e segundo as nossas instruções.</li>
                <li><strong className="text-foreground">Lista de exclusão</strong>: após a retirada, o teu e-mail pode permanecer numa lista de exclusão, na Intimate.pt e no Brevo, apenas para garantir que não voltas a receber comunicações promocionais. Esta lista não é usada para nenhuma outra finalidade.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">6. Partilha de Dados</h2>
              <p>Podemos partilhar os teus dados com:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li><strong className="text-foreground">Prestadores de serviços:</strong> processadores de pagamento, serviços de email, alojamento</li>
                <li><strong className="text-foreground">Autoridades:</strong> quando exigido por lei ou ordem judicial</li>
                <li><strong className="text-foreground">Transferência de negócio:</strong> em caso de fusão, aquisição ou venda de ativos</li>
              </ul>
              <p className="mt-4">
                <strong className="text-foreground">Nunca vendemos os teus dados pessoais a terceiros.</strong>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">7. Transferências Internacionais</h2>
              <p>
                Os teus dados podem ser transferidos para países fora do Espaço Económico Europeu (EEE). 
                Quando isso acontece, garantimos proteção adequada através de:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Cláusulas contratuais-tipo aprovadas pela Comissão Europeia</li>
                <li>Decisões de adequação da Comissão Europeia</li>
                <li>Outros mecanismos legais apropriados</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">8. Retenção de Dados</h2>
              <p>Conservamos os teus dados durante:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li><strong className="text-foreground">Dados de conta:</strong> enquanto a conta estiver ativa, mais 2 anos após eliminação</li>
                <li><strong className="text-foreground">Conteúdo publicado:</strong> enquanto a conta estiver ativa ou conforme necessário</li>
                <li><strong className="text-foreground">Dados de pagamento:</strong> conforme exigido por lei fiscal (7 anos)</li>
                <li><strong className="text-foreground">Logs de segurança:</strong> 90 dias</li>
                <li><strong className="text-foreground">Visitas com consentimento de estatísticas:</strong> conservadas para histórico. Passam a anónimas se retirares o consentimento</li>
                <li><strong className="text-foreground">Contadores estatísticos anónimos:</strong> sem limite (não são dados pessoais)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">9. Os Seus Direitos</h2>
              <p>Ao abrigo do RGPD, tem os seguintes direitos:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li><strong className="text-foreground">Acesso:</strong> solicitar cópia dos teus dados pessoais</li>
                <li><strong className="text-foreground">Retificação:</strong> corrigir dados inexatos ou incompletos</li>
                <li><strong className="text-foreground">Apagamento:</strong> solicitar eliminação dos teus dados ("direito ao esquecimento")</li>
                <li><strong className="text-foreground">Limitação:</strong> restringir o tratamento em certas circunstâncias</li>
                <li><strong className="text-foreground">Portabilidade:</strong> receber os teus dados num formato estruturado</li>
                <li><strong className="text-foreground">Oposição:</strong> opor-se ao tratamento baseado em interesses legítimos</li>
                <li><strong className="text-foreground">Retirar consentimento:</strong> a qualquer momento, sem afetar tratamento anterior</li>
              </ul>
              <p className="mt-4">
                Para exercer estes direitos, contacte-nos através de <strong className="text-ruby">geral@pimentadocelingerie.pt</strong>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">10. Segurança dos Dados</h2>
              <p>Implementamos medidas técnicas e organizativas para proteger os teus dados:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Encriptação de palavras-passe com bcrypt</li>
                <li>Comunicações seguras via HTTPS/TLS</li>
                <li>Autenticação por tokens JWT</li>
                <li>Acesso restrito aos dados por pessoal autorizado</li>
                <li>Monitorização contínua de segurança</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">11. Menores</h2>
              <p>
                A Plataforma destina-se exclusivamente a maiores de 18 anos. Não recolhemos 
                intencionalmente dados de menores. Se tomarmos conhecimento de que recolhemos 
                dados de um menor, eliminaremos essa informação imediatamente.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">12. Alterações a Esta Política</h2>
              <p>
                Podemos atualizar esta Política periodicamente. Notificá-lo-emos de alterações 
                significativas através de email ou aviso na Plataforma. A data da última atualização 
                está indicada no topo deste documento.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">13. Reclamações</h2>
              <p>
                Se considerar que o tratamento dos teus dados viola o RGPD, tem o direito de 
                apresentar reclamação junto da Comissão Nacional de Proteção de Dados (CNPD):
              </p>
              <div className="mt-3 p-4 bg-surface rounded-lg border border-border">
                <p><strong className="text-foreground">Comissão Nacional de Proteção de Dados</strong></p>
                <p>Av. D. Carlos I, 134 - 1.º</p>
                <p>1200-651 Lisboa</p>
                <p>Website: www.cnpd.pt</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">14. Contacto</h2>
              <p>
                Para questões sobre esta Política ou sobre os teus dados pessoais:
              </p>
              <p className="mt-3">
                <strong className="text-foreground">Email:</strong> geral@pimentadocelingerie.pt
              </p>
            </section>
          </div>

          {/* Related Links */}
          <div className="mt-12 pt-8 border-t border-border">
            <h3 className="text-lg font-semibold text-foreground mb-4">Documentos Relacionados</h3>
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/terms" 
                className="text-ruby hover:underline"
              >
                Termos de Utilização
              </Link>
              <Link 
                to="/cookies" 
                className="text-ruby hover:underline"
              >
                Política de Cookies
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
